<template #default>
    <form
        v-if="status.detail && !loading"
        @submit.prevent="propertyAssetUUID ? requestUpdate() : requestCreate()"
    >
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-home-outline mr-1"></i>
                    Basic Information
                </h6>
            </div>
            <div class="col-md-12">
                <b-form-group
                    :label="$t('label.image')"
                    label-cols="2"
                    label-align="left"
                >
                    <ImageUploader
                        :key="propertyAssetUUID"
                        :multiple="false"
                        :showImageTop="true"
                        :buttonText="$t('label.uploadX', [$t('label.image')])"
                        @images-changed="uploadImage"
                        @images-removed="removeImage"
                        :initial-images="createForm.property.image ? [createForm.property.image] : []"
                        ref="coverUploader"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Registered Owner"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.registeredOwner"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('module.company')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.company"
                        :class="{ 'is-invalid': submitted && !$v.createForm.property.company.required }"
                        autocomplete="off"
                    />
                    <b-form-invalid-feedback v-if="submitted && !$v.createForm.property.company.required" class="text-right">
                        {{ $t("message.requiredX", [$t("module.company")]) }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('module.group')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.property.groupUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="groupDdl"
                        :searchable="true"
                        :allow-empty="false"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.type')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.property.propertyTypeUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="propertyTypeDdl"
                        :searchable="true"
                        :allow-empty="false"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('module.location')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.property.locationUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="locationDdl"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
            <div class="col-md-6">
                <b-form-group
                    label="Address 1"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.address1"
                        autocomplete="off"
                    />
                </b-form-group>
                <b-form-group
                    label="Address 2"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.address2"
                        autocomplete="off"
                    />
                </b-form-group>
                <b-form-group
                    label="Address 3"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.address3"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Postcode"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.postcode"
                        :class="{ 'is-invalid': submitted && $v.createForm.property.postcode.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.property.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.property.postcode.decimal">{{ $t("message.invalidX", ["Posstcode"]) }}</span>
                    </div>
                </b-form-group>
                <b-form-group
                    label="State"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.state"
                        autocomplete="off"
                    />
                </b-form-group>
                <b-form-group
                    label="City"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.property.city"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Malaysia"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox 
                        v-model="createForm.property.isMalaysia"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Personal"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox
                        v-model="createForm.property.isPersonal"
                        switch
                        class="mt-2"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.description')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.property.description"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.remark')"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.property.remark"
                    />
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-domain mr-1"></i>
                    Asset Detail
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Tenure"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.asset.propertyTenureUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="propertyTenureDdl"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Category"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.asset.propertyCategoryUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="propertyCategoryDdl"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    :label="$t('label.status')"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.asset.propertyStatusUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="propertyStatusDdl"
                        :searchable="true"
                        :allow-empty="false"
                        :class="[
                            'cursor-pointer',
                            submitted && !$v.createForm.asset.propertyStatusUuid.required ? 'border border-danger' : ''
                        ]"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="text-danger small text-right">
                        <span v-if="!$v.createForm.asset.propertyStatusUuid.required">{{ $t("message.requiredX", [$t("label.status")]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Action"
                    label-cols="4"
                    label-align="left"
                >
                    <multiselect
                        v-model="createForm.asset.propertyActionUuid"
                        track-by="value"
                        label="text"
                        :placeholder="'Select'"
                        :select-label="'Select'"
                        :selected-label="'Selected'"
                        :deselect-label="'Select'"
                        :options="propertyActionDdl"
                        :searchable="true"
                        :allow-empty="false"
                        class="cursor-pointer"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="File ID"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.fileId"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Lot ID"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.lotId"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Hak Milik ID"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.hakMilikId"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Bandar Pekan Mukim"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.bandarPekanMukim"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-cash-multiple mr-1"></i>
                    Acquisition
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Purchase Date"
                    label-cols="4"
                    label-align="left"
                >
                    <DatePicker
                        v-model="createForm.asset.purchaseDate"
                        placeholder="yyyy-mm-dd"
                        class="cursor-pointer"
                        :config="{
                            format: 'YYYY-MM-DD',
                            useCurrent: false,
                            icons: {
                                time: 'mdi mdi-clock-outline',
                                date: 'mdi mdi-calendar',
                                up: 'mdi mdi-chevron-up',
                                down: 'mdi mdi-chevron-down',
                                previous: 'mdi mdi-chevron-left',
                                next: 'mdi mdi-chevron-right',
                                today: 'mdi mdi-calendar-check',
                                clear: 'mdi mdi-delete-outline',
                                close: 'mdi mdi-close',
                            },
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
            <div class="col-md-6">
                <b-form-group
                    label="Purchase Value PSF"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.purchaseValuePsf"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.purchaseValuePsf.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.purchaseValuePsf.decimal">{{ $t("message.invalidX", ["Purchase Value PSF"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Purchase Value Total"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.purchaseValueTotal"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.purchaseValueTotal.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.purchaseValueTotal.decimal">{{ $t("message.invalidX", ["Purchase Value Total"]) }}</span>
                    </div>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-chart-line mr-1"></i>
                    Revaluation
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Revaluation Date"
                    label-cols="4"
                    label-align="left"
                >
                    <DatePicker
                        v-model="createForm.asset.revaluationDate"
                        placeholder="yyyy-mm-dd"
                        class="cursor-pointer"
                        :config="{
                            format: 'YYYY-MM-DD',
                            useCurrent: false,
                            icons: {
                                time: 'mdi mdi-clock-outline',
                                date: 'mdi mdi-calendar',
                                up: 'mdi mdi-chevron-up',
                                down: 'mdi mdi-chevron-down',
                                previous: 'mdi mdi-chevron-left',
                                next: 'mdi mdi-chevron-right',
                                today: 'mdi mdi-calendar-check',
                                clear: 'mdi mdi-delete-outline',
                                close: 'mdi mdi-close',
                            },
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
            <div class="col-md-6">
                <b-form-group
                    label="Revaluation Value PSF"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.revaluationValuePsf"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.revaluationValuePsf.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.revaluationValuePsf.decimal">{{ $t("message.invalidX", ["Revaluation Value PSF"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Revaluation Value Total"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.revaluationValueTotal"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.revaluationValueTotal.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.revaluationValueTotal.decimal">{{ $t("message.invalidX", ["Revaluation Value Total"]) }}</span>
                    </div>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-finance mr-1"></i>
                    Valuation
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Market Value"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.marketValue"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.marketValue.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.marketValue.decimal">{{ $t("message.invalidX", ["Market Value"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="NBV Value"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.nbvValue"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.nbvValue.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.nbvValue.decimal">{{ $t("message.invalidX", ["Purchase Value Total"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Sold Price"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.soldPrice"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.soldPrice.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.soldPrice.decimal">{{ $t("message.invalidX", ["Sold Price"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Yield"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.yield"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.yield.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.yield.decimal">{{ $t("message.invalidX", ["Yield"]) }}</span>
                    </div>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-home-currency-usd mr-1"></i>
                    Rental Estimation
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Estimated Rental"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.estimatedRental"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.estimatedRental.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.estimatedRental.decimal">{{ $t("message.invalidX", ["Estimated Rental"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
            <div class="col-md-6">
                <b-form-group
                    label="Estimated Rental Monthly"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.estimatedRentalMonthly"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.estimatedRentalMonthly.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.estimatedRentalMonthly.decimal">{{ $t("message.invalidX", ["Estimated Rental Monthly"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Estimated Rental Yearly"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.estimatedRentalYearly"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.estimatedRentalYearly.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.estimatedRentalYearly.decimal">{{ $t("message.invalidX", ["Estimated Rental Yearly"]) }}</span>
                    </div>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-calendar-clock mr-1"></i>
                    Lease
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Lease Hold Expired"
                    label-cols="4"
                    label-align="left"
                >
                    <DatePicker
                        v-model="createForm.asset.leaseHoldExpiredDate"
                        placeholder="yyyy-mm-dd"
                        class="cursor-pointer"
                        :config="{
                            format: 'YYYY-MM-DD',
                            useCurrent: false,
                            icons: {
                                time: 'mdi mdi-clock-outline',
                                date: 'mdi mdi-calendar',
                                up: 'mdi mdi-chevron-up',
                                down: 'mdi mdi-chevron-down',
                                previous: 'mdi mdi-chevron-left',
                                next: 'mdi mdi-chevron-right',
                                today: 'mdi mdi-calendar-check',
                                clear: 'mdi mdi-delete-outline',
                                close: 'mdi mdi-close',
                            },
                        }"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
            <div class="col-md-6">
                <b-form-group
                    label="Lease Year"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.leaseYear"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.leaseYear.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.leaseYear.decimal">{{ $t("message.invalidX", ["Lease Year"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Lease Year Remaining"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.leaseYearRemaining"
                        :class="{ 'is-invalid': submitted && $v.createForm.asset.leaseYearRemaining.$error }"
                        autocomplete="off"
                    />
                    <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                        <span v-if="!$v.createForm.asset.leaseYearRemaining.decimal">{{ $t("message.invalidX", ["Lease Year Remaining"]) }}</span>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Grade"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-input
                        v-model.trim="createForm.asset.grade"
                        autocomplete="off"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6"></div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-resize mr-1"></i>
                    Area / Size
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Building Size"
                    label-cols="4"
                    label-align="left"
                >
                    <div class="row">
                        <div class="col-md-6 mb-1">
                            <b-form-input
                                v-model.trim="createForm.asset.buildingSize"
                                :class="{ 'is-invalid': submitted && $v.createForm.asset.buildingSize.$error }"
                                autocomplete="off"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                                <span v-if="!$v.createForm.asset.buildingSize.decimal">{{ $t("message.invalidX", [""]) }}</span>
                            </div>
                        </div>
                        <div class="col-md-6 mb-1">
                            <multiselect
                                v-model="createForm.asset.buildingSizeUnitUuid"
                                track-by="value"
                                label="text"
                                :placeholder="'Unit'"
                                :select-label="'Select'"
                                :selected-label="'Selected'"
                                :deselect-label="'Select'"
                                :options="propertyAreaUnitDdl"
                                :searchable="true"
                                :allow-empty="false"
                                :class="[
                                    'cursor-pointer',
                                    submitted && !$v.createForm.asset.buildingSizeUnitUuid.required ? 'border border-danger' : ''
                                ]"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="text-danger small text-right">
                                <span v-if="!$v.createForm.asset.buildingSizeUnitUuid.required">{{ $t("message.requiredX", ["Unit"]) }}</span>
                            </div>
                        </div>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Land Size"
                    label-cols="4"
                    label-align="left"
                >
                    <div class="row">
                        <div class="col-md-6 mb-1">
                            <b-form-input
                                v-model.trim="createForm.asset.landSize"
                                :class="{ 'is-invalid': submitted && $v.createForm.asset.landSize.$error }"
                                autocomplete="off"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                                <span v-if="!$v.createForm.asset.landSize.decimal">{{ $t("message.invalidX", [""]) }}</span>
                            </div>
                        </div>
                        <div class="col-md-6 mb-1">
                            <multiselect
                                v-model="createForm.asset.landSizeUnitUuid"
                                track-by="value"
                                label="text"
                                :placeholder="'Unit'"
                                :select-label="'Select'"
                                :selected-label="'Selected'"
                                :deselect-label="'Select'"
                                :options="propertyAreaUnitDdl"
                                :searchable="true"
                                :allow-empty="false"
                                :class="[
                                    'cursor-pointer',
                                    submitted && !$v.createForm.asset.landSizeUnitUuid.required ? 'border border-danger' : ''
                                ]"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="text-danger small text-right">
                                <span v-if="!$v.createForm.asset.landSizeUnitUuid.required">{{ $t("message.requiredX", ["Unit"]) }}</span>
                            </div>
                        </div>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Utilize Building Size"
                    label-cols="4"
                    label-align="left"
                >
                    <div class="row">
                        <div class="col-md-4 mb-1">
                            <b-form-input
                                v-model.trim="createForm.asset.utilizeBuildingSize"
                                :class="{ 'is-invalid': submitted && $v.createForm.asset.utilizeBuildingSize.$error }"
                                autocomplete="off"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                                <span v-if="!$v.createForm.asset.utilizeBuildingSize.decimal">{{ $t("message.invalidX", [""]) }}</span>
                            </div>
                        </div>
                        <div class="col-md-4 mb-1">
                            <multiselect
                                v-model="createForm.asset.utilizeBuildingSizeUnitUuid"
                                track-by="value"
                                label="text"
                                :placeholder="'Unit'"
                                :select-label="'Select'"
                                :selected-label="'Selected'"
                                :deselect-label="'Select'"
                                :options="propertyAreaUnitDdl"
                                :searchable="true"
                                :allow-empty="false"
                                :class="[
                                    'cursor-pointer',
                                    submitted && !$v.createForm.asset.utilizeBuildingSizeUnitUuid.required ? 'border border-danger' : ''
                                ]"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="text-danger small text-right">
                                <span v-if="!$v.createForm.asset.utilizeBuildingSizeUnitUuid.required">{{ $t("message.requiredX", ["Unit"]) }}</span>
                            </div>
                        </div>
                        <div class="col-md-4 mb-1">
                            <b-form-input
                                v-model.trim="createForm.asset.utilizeBuildingPercentage"
                                placeholder="%"
                                :class="{ 'is-invalid': submitted && $v.createForm.asset.utilizeBuildingPercentage.$error }"
                                autocomplete="off"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                                <span v-if="!$v.createForm.asset.utilizeBuildingPercentage.decimal">{{ $t("message.invalidX", ["Percentage"]) }}</span>
                            </div>
                        </div>
                    </div>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Utilize Land Size"
                    label-cols="4"
                    label-align="left"
                >
                    <div class="row">
                        <div class="col-md-4 mb-1">
                            <b-form-input
                                v-model.trim="createForm.asset.utilizeLandSize"
                                :class="{ 'is-invalid': submitted && $v.createForm.asset.utilizeLandSize.$error }"
                                autocomplete="off"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                                <span v-if="!$v.createForm.asset.utilizeLandSize.decimal">{{ $t("message.invalidX", [""]) }}</span>
                            </div>
                        </div>
                        <div class="col-md-4 mb-1">
                            <multiselect
                                v-model="createForm.asset.utilizeLandSizeUnitUuid"
                                track-by="value"
                                label="text"
                                :placeholder="'Unit'"
                                :select-label="'Select'"
                                :selected-label="'Selected'"
                                :deselect-label="'Select'"
                                :options="propertyAreaUnitDdl"
                                :searchable="true"
                                :allow-empty="false"
                                :class="[
                                    'cursor-pointer',
                                    submitted && !$v.createForm.asset.utilizeLandSizeUnitUuid.required ? 'border border-danger' : ''
                                ]"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="text-danger small text-right">
                                <span v-if="!$v.createForm.asset.utilizeLandSizeUnitUuid.required">{{ $t("message.requiredX", ["Unit"]) }}</span>
                            </div>
                        </div>
                        <div class="col-md-4 mb-1">
                            <b-form-input
                                v-model.trim="createForm.asset.utilizeLandPercentage"
                                placeholder="%"
                                :class="{ 'is-invalid': submitted && $v.createForm.asset.utilizeLandPercentage.$error }"
                                autocomplete="off"
                            />
                            <div v-if="submitted && $v.createForm.asset.$error" class="invalid-feedback text-right">
                                <span v-if="!$v.createForm.asset.utilizeLandPercentage.decimal">{{ $t("message.invalidX", ["Percentage"]) }}</span>
                            </div>
                        </div>
                    </div>
                </b-form-group>
            </div>
        </div>
        <hr />
        <div class="row">
            <div class="col-md-12">
                <h6 class="font-weight-bold text-info">
                    <i class="mdi mdi-gavel mr-1"></i>
                    Compliant
                </h6>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Compliance"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-checkbox
                        v-model="createForm.asset.isCompliance"
                        switch
                        class="mt-2"
                        @change="value => !value && (createForm.asset.complianceRemark = null)"
                    />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group
                    label="Compliance Remark"
                    label-cols="4"
                    label-align="left"
                >
                    <b-form-textarea
                        v-model.trim="createForm.asset.complianceRemark"
                        :disabled="!createForm.asset.isCompliance"
                        :class="[createForm.asset.isCompliance ? 'bg-transparent fade-in' : 'bg-light']"
                    />
                </b-form-group>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-md-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-md-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">
                    {{ propertyAssetUUID ? $t('label.updateX', []) : $t('label.createX', []) }}
                </span>
                <div v-else>
                    <b-spinner small />
                </div>
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import DatePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import ImageUploader from '@/components/upload-image';
import Multiselect from "vue-multiselect";
import { required, requiredIf, decimal } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { propertyAssetMethods, lookupMethods } from "@/state/api";
import { PROPERTY_ASSET_MODULE } from "@/state/api/propertyAsset.state";

export default {
    props: {
        propertyAssetUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                property: {
                    company: { required },
                    lat: { decimal },
                    lng: { decimal},
                    postcode: { decimal },
                },
                asset: {
                    purchaseValuePsf: { decimal },
                    nbvValue: { decimal },
                    marketValue: { decimal },
                    utilizeBuildingSize: { decimal },
                    utilizeBuildingSizeUnitUuid: {
                        required: requiredIf(function (asset) { return required(asset.utilizeBuildingSize); }),
                    },
                    utilizeBuildingPercentage: { decimal },
                    landSize: { decimal },
                    landSizeUnitUuid: {
                        required: requiredIf(function (asset) { return required(asset.landSize); }),
                    },
                    buildingSize: { decimal },
                    buildingSizeUnitUuid: {
                        required: requiredIf(function (asset) { return required(asset.buildingSize); }),
                    },
                    utilizeLandSize: { decimal },
                    utilizeLandSizeUnitUuid: {
                        required: requiredIf(function (asset) { return required(asset.utilizeLandSize); }),
                    },
                    utilizeLandPercentage: { decimal },
                    purchaseValueTotal: { decimal },
                    soldPrice: { decimal },
                    revaluationValuePsf: { decimal },
                    revaluationValueTotal: { decimal },
                    yield: { decimal },
                    estimatedRental: { decimal },
                    estimatedRentalMonthly: { decimal },
                    estimatedRentalYearly: { decimal },
                    leaseYear: { decimal },
                    leaseYearRemaining: { decimal },
                    propertyStatusUuid: { required },
                },
            },
        };
    },
    components: { ImageUploader, Multiselect, DatePicker },
    async created() {
        await this.lookupGroup();
        await this.lookupPropertyType();
        await this.lookupLocation();
        await this.lookupPropertyTenure();
        await this.lookupPropertyCategory();
        await this.lookupPropertyStatus();
        await this.lookupPropertyAction();
        await this.lookupPropertyAreaUnit();
        await this.getPropertyAssetData();

        this.loading = false;
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
            loading: !!this.propertyAssetUUID,
        };
    },
    methods: {
        ...propertyAssetMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: null,
                property: {
                    company: null,
                    registeredOwner: null,
                    groupUuid: null,
                    propertyTypeUuid: null,
                    locationUuid: null,
                    description: null,
                    remark: null,
                    address1: null,
                    address2: null,
                    address3: null,
                    city: null,
                    state: null,
                    postcode: null,
                    lat: null,
                    lng: null,
                    isAsset: 1,
                    isTenanted: 0,
                    isMalaysia: false,
                    isPersonal: false,
                    image: null,
                },
                asset: {
                    propertyTenureUuid: null,
                    propertyCategoryUuid: null,
                    propertyStatusUuid: null,
                    propertyActionUuid: null,
                    utilizeBuildingSizeUnitUuid: null,
                    utilizeLandSizeUnitUuid: null,
                    buildingSizeUnitUuid: null,
                    landSizeUnitUuid: null,
                    fileId: null,
                    lotId: null,
                    hakMilikId: null,
                    purchaseDate: null,
                    purchaseValuePsf: null,
                    purchaseValueTotal: null,
                    nbvValue: null,
                    soldPrice: null,
                    revaluationDate: null,
                    buildingSize: null,
                    landSize: null,
                    utilizeBuildingSize: null,
                    utilizeBuildingPercentage: null,
                    utilizeLandSize: null,
                    utilizeLandPercentage: null,
                    marketValue: null,
                    leaseHoldExpiredDate: null,
                    bandarPekanMukim: null,
                    isCompliance: false,
                    revaluationValuePsf: null,
                    revaluationValueTotal: null,
                    yield: null,
                    estimatedRental: null,
                    estimatedRentalMonthly: null,
                    estimatedRentalYearly: null,
                    leaseYear: null,
                    leaseYearRemaining: null,
                    grade: null,
                    complianceRemark: null,
                },
            };
        },
        async getPropertyAssetData() {
            if (this.propertyAssetUUID) {
                await this.getPropertyAsset({ uuid: this.propertyAssetUUID })
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                property: {
                                    company: this.data.company,
                                    registeredOwner: this.data.registeredOwner,
                                    description: this.data.description,
                                    remark: this.data.remark,
                                    address1: this.data.address1,
                                    address2: this.data.address2,
                                    address3: this.data.address3,
                                    city: this.data.city,
                                    state: this.data.state,
                                    postcode: this.data.postcode,
                                    lat: this.data.lat,
                                    lng: this.data.lng,
                                    isAsset: 1,
                                    isTenanted: 0,
                                    isMalaysia: this.data.isMalaysia,
                                    isPersonal: this.data.isPersonal,
                                    image: this.data.imageUrl,
                                    groupUuid: this.data.group ? {
                                        value: this.data.group.uuid,
                                        text: this.data.group.name,
                                    } : null,
                                    propertyTypeUuid: this.data.propertyType ? {
                                        value: this.data.propertyType.uuid,
                                        text: this.data.propertyType.name,
                                    } : null,
                                    locationUuid: this.data.location ? {
                                        value: this.data.location.uuid,
                                        text: this.data.location.name,
                                    } : null,
                                },
                                asset: {
                                    ...this.data.asset,
                                    isCompliance: this.data.asset?.isCompliance,
                                    propertyTenureUuid: this.data.asset?.propertyTenure ? {
                                        value: this.data.asset.propertyTenure.uuid,
                                        text: this.data.asset.propertyTenure.name,
                                    } : null,
                                    propertyCategoryUuid: this.data.asset?.propertyCategory ? {
                                        value: this.data.asset.propertyCategory.uuid,
                                        text: this.data.asset.propertyCategory.name,
                                    } : null,
                                    propertyStatusUuid: this.data.asset?.propertyStatus ? {
                                        value: this.data.asset.propertyStatus.uuid,
                                        text: this.data.asset.propertyStatus.name,
                                    } : null,
                                    propertyActionUuid: this.data.asset?.propertyAction ? {
                                        value: this.data.asset.propertyAction.uuid,
                                        text: this.data.asset.propertyAction.name,
                                    } : null,
                                    utilizeBuildingSizeUnitUuid: this.data.asset?.utilizeBuildingSizeUnit ? {
                                        value: this.data.asset.utilizeBuildingSizeUnit.uuid,
                                        text: this.data.asset.utilizeBuildingSizeUnit.name,
                                    } : null,
                                    utilizeLandSizeUnitUuid: this.data.asset?.utilizeLandSizeUnit ? {
                                        value: this.data.asset.utilizeLandSizeUnit.uuid,
                                        text: this.data.asset.utilizeLandSizeUnit.name,
                                    } : null,
                                    buildingSizeUnitUuid: this.data.asset?.buildingSizeUnit ? {
                                        value: this.data.asset.buildingSizeUnit.uuid,
                                        text: this.data.asset.buildingSizeUnit.name,
                                    } : null,
                                    landSizeUnitUuid: this.data.asset?.landSizeUnit ? {
                                        value: this.data.asset.landSizeUnit.uuid,
                                        text: this.data.asset.landSizeUnit.name,
                                    } : null,
                                },
                            };
                        }
                    });
            }
        },
        uploadImage(image) {
            this.createForm.property.image = image[0]?.file;
        },
        removeImage() {
            this.createForm.property.image = null;
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    property: {
                        ...this.createForm.property,
                        groupUuid: this.createForm.property.groupUuid?.value,
                        propertyTypeUuid: this.createForm.property.propertyTypeUuid?.value,
                        locationUuid: this.createForm.property.locationUuid?.value,
                        isMalaysia: this.createForm.property.isMalaysia ? 1 : 0,
                        isPersonal: this.createForm.property.isPersonal ? 1 : 0,
                    },
                    asset: {
                        ...this.createForm.asset,
                        propertyTenureUuid: this.createForm.asset.propertyTenureUuid?.value,
                        propertyCategoryUuid: this.createForm.asset.propertyCategoryUuid?.value,
                        propertyStatusUuid: this.createForm.asset.propertyStatusUuid?.value,
                        propertyActionUuid: this.createForm.asset.propertyActionUuid?.value,
                        utilizeBuildingSizeUnitUuid: this.createForm.asset.utilizeBuildingSizeUnitUuid?.value,
                        utilizeLandSizeUnitUuid: this.createForm.asset.utilizeLandSizeUnitUuid?.value,
                        buildingSizeUnitUuid: this.createForm.asset.buildingSizeUnitUuid?.value,
                        landSizeUnitUuid: this.createForm.asset.landSizeUnitUuid?.value,
                        isCompliance: this.createForm.asset.isCompliance ? 1 : 0,
                    },
                };
                this.createPropertyAsset({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("saved");
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    uuid: this.createForm.uuid,
                    property: {
                        ...this.createForm.property,
                        groupUuid: this.createForm.property.groupUuid?.value,
                        propertyTypeUuid: this.createForm.property.propertyTypeUuid?.value,
                        locationUuid: this.createForm.property.locationUuid?.value,
                        isMalaysia: this.createForm.property.isMalaysia ? 1 : 0,
                        isPersonal: this.createForm.property.isPersonal ? 1 : 0,
                    },
                    asset: {
                        ...this.createForm.asset,
                        propertyTenureUuid: this.createForm.asset.propertyTenureUuid?.value,
                        propertyCategoryUuid: this.createForm.asset.propertyCategoryUuid?.value,
                        propertyStatusUuid: this.createForm.asset.propertyStatusUuid?.value,
                        propertyActionUuid: this.createForm.asset.propertyActionUuid?.value,
                        utilizeBuildingSizeUnitUuid: this.createForm.asset.utilizeBuildingSizeUnitUuid?.value,
                        utilizeLandSizeUnitUuid: this.createForm.asset.utilizeLandSizeUnitUuid?.value,
                        buildingSizeUnitUuid: this.createForm.asset.buildingSizeUnitUuid?.value,
                        landSizeUnitUuid: this.createForm.asset.landSizeUnitUuid?.value,
                        isCompliance: this.createForm.asset.isCompliance ? 1 : 0,
                    },
                };

                this.updatePropertyAsset({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("saved");
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
        },
    },
    computed: {
        ...mapState(PROPERTY_ASSET_MODULE, ['status']),
        data() {
            return this.$store.state.api.propertyAsset.details;
        },
        groupDdl() {
            return this.$store.state.api.lookup.groupDdl || [];
        },
        propertyTypeDdl() {
            return this.$store.state.api.lookup.propertyTypeDdl || [];
        },
        locationDdl() {
            return this.$store.state.api.lookup.locationDdl || [];
        },
        propertyTenureDdl() {
            return this.$store.state.api.lookup.propertyTenureDdl || [];
        },
        propertyCategoryDdl() {
            return this.$store.state.api.lookup.propertyCategoryDdl || [];
        },
        propertyStatusDdl() {
            return this.$store.state.api.lookup.propertyStatusDdl || [];
        },
        propertyActionDdl() {
            return this.$store.state.api.lookup.propertyActionDdl || [];
        },
        propertyAreaUnitDdl() {
            return this.$store.state.api.lookup.propertyAreaUnitDdl || [];
        },
    },
}
</script>
